import React, { useEffect, useState, useContext } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Stack,
  Textarea,
  useDisclosure,
  useToast,
  Box,
  Heading,
  VStack,
  HStack,
  Icon,
  Divider,
  ModalCloseButton
} from '@chakra-ui/react';
import { MdChat, MdCheckCircle, MdSend } from 'react-icons/md';
import AuthContext from '../services/AuthContext';
import axiosInstance from '../services/axiosInstance';

const PRIMARY = '#F2C919';
const DARK = '#2C3E50';

const SurveyModal = ({ isOpen, onClose, surveyId }) => {
  const { user } = useContext(AuthContext);
  const [survey, setSurvey] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      // Fetch the survey based on the surveyId
      console.log(surveyId)
      axiosInstance
        .get(`/surveys/${surveyId}`)
        .then((response) => {
          setSurvey(response.data);
        })
        .catch((error) => {
         
        });
    }
  }, [isOpen, surveyId]);

  const handleResponseChange = (questionId, value) => {
    setResponses((prev) => {
      const existingResponse = prev.find((response) => response.questionId === questionId);
      if (existingResponse) {
        // Update the response if it already exists
        return prev.map((response) =>
          response.questionId === questionId ? { ...response, answer: value } : response
        );
      } else {
        // Add new response
        return [...prev, { questionId, answer: value }];
      }
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Submit the responses to the backend, matching the schema format
      await axiosInstance.post('/surveys/responses/submit', {
        userId: user._id,
        surveyId: survey._id,
        responses,
      });

      toast({
        title: 'تم إرسال الاستبيان',
        description: 'تم إرسال ردودك بنجاح.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      toast({
        title: 'خطأ في إرسال الاستبيان',
        description: 'حدثت مشكلة أثناء إرسال ردودك.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!survey) return null;

  // Check if the survey language is Arabic
  const isArabic = survey.title && /[\u0600-\u06FF]/.test(survey.title);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent dir={isArabic ? 'rtl' : 'ltr'}>
        <ModalHeader textAlign={isArabic ? 'right' : 'left'}>{survey.title}</ModalHeader>
        <ModalBody>
          {survey.questions.map((question) => (
            <FormControl key={question._id} mb={4}>
              <FormLabel textAlign={isArabic ? 'right' : 'left'}>{question.questionText}</FormLabel> 
              {question.type === 'multiple-choice' ? (
                <RadioGroup
                  onChange={(value) => handleResponseChange(question._id, value)}
                  value={responses.find((response) => response.questionId === question._id)?.answer || ''}
                >
                  <Stack spacing={4} direction={isArabic ? 'column-reverse' : 'column'}> {/* Adjusted for RTL */}
                    {question.options.map((option, index) => (
                      <Radio key={index} value={option} dir={isArabic ? 'rtl' : 'ltr'}> {/* Adjusted for RTL */}
                        {option}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
              ) : (
                <Textarea
                  value={responses.find((response) => response.questionId === question._id)?.answer || ''}
                  onChange={(e) => handleResponseChange(question._id, e.target.value)}
                  placeholder={isArabic ? 'إجابتك' : 'Your response'} // Translated placeholder
                  dir={isArabic ? 'rtl' : 'ltr'} // Adjusted for RTL
                />
              )}
            </FormControl>
          ))}
        </ModalBody>

        <ModalFooter justifyContent={isArabic ? 'flex-end' : 'flex-start'}> {/* Adjusted for RTL */}
          <Button
            colorScheme="blue"
            ml={isArabic ? 0 : 3} // Adjusted margin for RTL
            mr={isArabic ? 3 : 0} // Adjusted margin for RTL
            onClick={onClose}
            disabled={loading}
          >
            {isArabic ? 'إلغاء' : 'Cancel'} {/* Translated */}
          </Button>
          <Button
            colorScheme="green"
            onClick={handleSubmit}
            isLoading={loading}
          >
            {isArabic ? 'إرسال' : 'Submit'} {/* Translated */}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SurveyModal;



